import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  PenLine, Type, Calendar, CheckSquare, User, Trash2, Save, ChevronLeft, MousePointer,
  Pencil, Highlighter, MessageSquare, ArrowUpRight, Square, Eraser,
} from 'lucide-react';
import API, { formatError } from '../utils/api';
import { toast } from 'sonner';
import * as pdfjsLib from 'pdfjs-dist';
import AnnotationLayer from '../components/AnnotationLayer';

pdfjsLib.GlobalWorkerOptions.workerSrc =
  'https://unpkg.com/pdfjs-dist@3.11.174/legacy/build/pdf.worker.min.js';

const FIELD_TYPES = [
  { type: 'signature', icon: PenLine, label: 'Signature', color: '#1d4ed8', bg: 'rgba(29,78,216,0.1)' },
  { type: 'initials',  icon: PenLine, label: 'Initials',  color: '#0891b2', bg: 'rgba(8,145,178,0.1)' },
  { type: 'text',      icon: Type,    label: 'Text',      color: '#7c3aed', bg: 'rgba(124,58,237,0.1)' },
  { type: 'date',      icon: Calendar,label: 'Date',      color: '#059669', bg: 'rgba(5,150,105,0.1)' },
  { type: 'name',      icon: User,    label: 'Name',      color: '#0f766e', bg: 'rgba(15,118,110,0.1)' },
  { type: 'checkbox',  icon: CheckSquare, label: 'Checkbox', color: '#d97706', bg: 'rgba(217,119,6,0.1)' },
];

const ANNOTATION_TOOLS = [
  { type: 'pen',       icon: Pencil,       label: 'Pen',         hint: 'Freehand draw' },
  { type: 'highlight', icon: Highlighter,  label: 'Highlight',   hint: 'Drag to highlight' },
  { type: 'text',      icon: Type,         label: 'Text Box',    hint: 'Click to place text' },
  { type: 'sticky',    icon: MessageSquare,label: 'Sticky Note', hint: 'Click to pin a note' },
  { type: 'arrow',     icon: ArrowUpRight, label: 'Arrow',       hint: 'Drag to draw arrow' },
  { type: 'rect',      icon: Square,       label: 'Rectangle',   hint: 'Drag to draw rect' },
  { type: 'eraser',    icon: Eraser,       label: 'Eraser',      hint: 'Click to erase' },
];

const ANN_COLORS = ['#ef4444','#f97316','#fbbf24','#22c55e','#3b82f6','#8b5cf6','#0f172a'];

export default function DocumentEditor() {
  const { docId } = useParams();
  const navigate = useNavigate();

  const [doc, setDoc]             = useState(null);
  const [pages, setPages]         = useState([]);
  const [pdfError, setPdfError]   = useState(null);

  // Form fields
  const [fields, setFields]           = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedField, setSelectedField] = useState(null);

  // Annotations
  const [annotations, setAnnotations] = useState([]);
  const [annotationTool, setAnnotationTool] = useState(null);
  const [annotationColor, setAnnotationColor] = useState('#ef4444');
  const [strokeWidth, setStrokeWidth] = useState(3);

  // Editor mode: 'fields' | 'annotate'
  const [editorMode, setEditorMode] = useState('fields');
  const [saving, setSaving] = useState(false);

  const canvasRefs    = useRef([]);
  const containerRefs = useRef([]);

  /* ── Load document ─────────────────────────────────────────── */
  useEffect(() => {
    API.get(`/documents/${docId}`)
      .then(r => {
        setDoc(r.data);
        setFields(r.data.fields || []);
        setAnnotations(r.data.annotations || []);
        loadPdf(r.data.doc_id || r.data.id);
      })
      .catch(() => toast.error('Failed to load document'));
  }, [docId]);

  const loadPdf = async (id) => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/documents/${id}/file`;
      const resp = await fetch(url, { credentials: 'include' });
      if (!resp.ok) throw new Error();
      const buf  = await resp.arrayBuffer();
      const pdf  = await pdfjsLib.getDocument({ data: buf }).promise;
      const arr  = [];
      for (let i = 1; i <= pdf.numPages; i++) arr.push(await pdf.getPage(i));
      setPages(arr);
    } catch {
      setPdfError('Could not render PDF preview — fields and annotations still work.');
    }
  };

  /* ── Render PDF pages onto canvases ────────────────────────── */
  useEffect(() => {
    const tasks = [];
    pages.forEach((page, i) => {
      const canvas = canvasRefs.current[i];
      if (!canvas) return;
      const vp = page.getViewport({ scale: 1.4 });
      canvas.width  = vp.width;
      canvas.height = vp.height;
      const task = page.render({ canvasContext: canvas.getContext('2d'), viewport: vp });
      // Swallow RenderingCancelledException thrown on cleanup cancel
      if (task.promise) task.promise.catch(err => {
        if (err?.name !== 'RenderingCancelledException') console.error(err);
      });
      tasks.push(task);
    });
    return () => tasks.forEach(t => { try { t.cancel(); } catch (_) {} });
  }, [pages]);

  /* ── Field placement on page click ─────────────────────────── */
  const handlePageClick = useCallback((e, pageIdx) => {
    if (editorMode !== 'fields' || !selectedType) return;
    const container = containerRefs.current[pageIdx];
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width)  * 100;
    const y = ((e.clientY - rect.top)  / rect.height) * 100;
    const isSmall = selectedType === 'checkbox';
    setFields(prev => [...prev, {
      id: Date.now().toString(),
      type: selectedType,
      page: pageIdx,
      x: Math.max(0, Math.min(x, 85)),
      y: Math.max(0, Math.min(y, 92)),
      width:  isSmall ? 5 : (selectedType === 'signature' ? 22 : 18),
      height: isSmall ? 4 : (selectedType === 'signature' ? 8  : 5),
      required: true,
      recipient_id: null,
      label: selectedType,
    }]);
  }, [editorMode, selectedType]);

  const removeField = (id) => {
    setFields(prev => prev.filter(f => f.id !== id));
    if (selectedField?.id === id) setSelectedField(null);
  };

  /* ── Annotation handlers ───────────────────────────────────── */
  const handleAddAnnotation    = useCallback(ann => setAnnotations(prev => [...prev, ann]), []);
  const handleRemoveAnnotation = useCallback(id  => setAnnotations(prev => prev.filter(a => a.id !== id)), []);

  /* ── Save fields + annotations ─────────────────────────────── */
  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        API.put(`/documents/${docId}/fields`, fields),
        API.put(`/documents/${docId}/annotations`, { annotations }),
      ]);
      toast.success('Document saved');
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  /* ── Mode switch helper ────────────────────────────────────── */
  const switchMode = (m) => {
    setEditorMode(m);
    setSelectedType(null);
    setAnnotationTool(null);
  };

  const ft = FIELD_TYPES.find(f => f.type === selectedField?.type);
  const activeAnnotationTool = editorMode === 'annotate' ? annotationTool : null;

  /* ── Render ────────────────────────────────────────────────── */
  return (
    <div className="flex h-screen bg-slate-100 overflow-hidden">

      {/* ═══ Sidebar ════════════════════════════════════════════ */}
      <div className="w-56 bg-white border-r border-slate-200 flex flex-col flex-shrink-0 overflow-hidden">

        {/* Header */}
        <div className="flex items-center gap-2 p-4 border-b border-slate-200 flex-shrink-0">
          <button
            onClick={() => navigate('/documents')}
            className="p-1 text-slate-400 hover:text-slate-700 rounded transition-colors"
            data-testid="back-to-documents"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-slate-700">Document Editor</p>
            <p className="text-xs text-slate-400 truncate">{doc?.name || 'Loading…'}</p>
          </div>
        </div>

        {/* Mode toggle */}
        <div className="px-3 pt-3 pb-0 flex-shrink-0">
          <div className="flex rounded-sm bg-slate-100 p-0.5 gap-0.5">
            {[{ id: 'fields', label: 'Fields' }, { id: 'annotate', label: 'Annotate' }].map(m => (
              <button
                key={m.id}
                onClick={() => switchMode(m.id)}
                className={`flex-1 py-1.5 text-xs font-medium rounded-sm transition-colors ${
                  editorMode === m.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid={`mode-${m.id}`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Form Fields panel ─────────────────────────────── */}
        {editorMode === 'fields' && (
          <div className="flex-1 overflow-y-auto p-3">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Arm a field · click page</p>
            <div className="space-y-1">
              {FIELD_TYPES.map(ft => {
                const Icon = ft.icon;
                return (
                  <button
                    key={ft.type}
                    onClick={() => setSelectedType(prev => prev === ft.type ? null : ft.type)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-colors ${
                      selectedType === ft.type ? 'text-white' : 'text-slate-700 hover:bg-slate-50'
                    }`}
                    style={selectedType === ft.type ? { backgroundColor: ft.color } : {}}
                    data-testid={`field-type-${ft.type}`}
                  >
                    <Icon size={14} /> {ft.label}
                  </button>
                );
              })}
            </div>

            {selectedType && (
              <div className="mt-3 text-xs text-blue-700 bg-blue-50 rounded-sm px-2 py-1.5 flex items-center gap-1.5">
                <MousePointer size={12} /> Click on the page to place
              </div>
            )}

            {/* Selected field properties */}
            {selectedField && (
              <div className="mt-4 p-3 bg-slate-50 rounded-sm border border-slate-200">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Field Properties</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: ft?.color }} />
                    <span className="text-sm text-slate-700 capitalize">{selectedField.type}</span>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={selectedField.required}
                      onChange={e => setFields(prev => prev.map(f =>
                        f.id === selectedField.id ? { ...f, required: e.target.checked } : f
                      ))}
                      className="rounded"
                    />
                    Required
                  </label>
                  <button
                    onClick={() => removeField(selectedField.id)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-red-500 hover:bg-red-50 rounded-sm text-sm transition-colors"
                    data-testid="delete-field-btn"
                  >
                    <Trash2 size={13} /> Remove Field
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Annotation panel ──────────────────────────────── */}
        {editorMode === 'annotate' && (
          <div className="flex-1 overflow-y-auto p-3">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Annotation Tools</p>
            <div className="space-y-1">
              {ANNOTATION_TOOLS.map(at => {
                const Icon = at.icon;
                return (
                  <button
                    key={at.type}
                    onClick={() => setAnnotationTool(prev => prev === at.type ? null : at.type)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-colors ${
                      annotationTool === at.type ? 'bg-slate-800 text-white' : 'text-slate-700 hover:bg-slate-50'
                    }`}
                    title={at.hint}
                    data-testid={`annotation-tool-${at.type}`}
                  >
                    <Icon size={14} /> {at.label}
                  </button>
                );
              })}
            </div>

            {/* Color picker */}
            <div className="mt-4">
              <p className="text-xs text-slate-400 mb-2">Color</p>
              <div className="flex gap-1.5 flex-wrap">
                {ANN_COLORS.map(c => (
                  <button
                    key={c}
                    onClick={() => setAnnotationColor(c)}
                    className={`w-5 h-5 rounded-full border-2 transition-transform ${
                      annotationColor === c ? 'scale-125 border-slate-600' : 'border-transparent hover:scale-110'
                    }`}
                    style={{ background: c }}
                    data-testid={`ann-color-${c.slice(1)}`}
                  />
                ))}
              </div>
            </div>

            {/* Stroke width (not for text/sticky/eraser/highlight) */}
            {annotationTool && !['text', 'sticky', 'eraser', 'highlight'].includes(annotationTool) && (
              <div className="mt-4">
                <p className="text-xs text-slate-400 mb-1.5">Thickness: {strokeWidth}px</p>
                <input
                  type="range" min="1" max="8" value={strokeWidth}
                  onChange={e => setStrokeWidth(parseInt(e.target.value))}
                  className="w-full accent-slate-700"
                  data-testid="stroke-width-slider"
                />
              </div>
            )}

            {annotationTool && (
              <div className="mt-3 text-xs text-slate-600 bg-slate-50 rounded-sm px-2 py-1.5 leading-relaxed">
                {ANNOTATION_TOOLS.find(t => t.type === annotationTool)?.hint}
              </div>
            )}

            {annotations.length > 0 && (
              <button
                onClick={() => { if (window.confirm('Clear all annotations on this document?')) setAnnotations([]); }}
                className="mt-4 w-full text-xs text-red-500 hover:text-red-700 hover:bg-red-50 py-1.5 rounded-sm transition-colors"
                data-testid="clear-annotations-btn"
              >
                Clear All Annotations
              </button>
            )}
          </div>
        )}

        {/* Save footer */}
        <div className="p-3 border-t border-slate-100 flex-shrink-0">
          <p className="text-xs text-slate-400 mb-2">
            {fields.length} field{fields.length !== 1 ? 's' : ''} · {annotations.length} annotation{annotations.length !== 1 ? 's' : ''}
          </p>
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white py-2 rounded-sm text-sm font-medium transition-colors"
            data-testid="save-document-btn"
          >
            <Save size={14} />
            {saving ? 'Saving…' : 'Save Document'}
          </button>
        </div>
      </div>

      {/* ═══ PDF Canvas Area ════════════════════════════════════ */}
      <div
        className="flex-1 overflow-y-auto p-6 flex flex-col items-center gap-6"
        data-testid="pdf-canvas-area"
      >
        {pdfError && (
          <div className="w-full max-w-3xl bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded-sm text-sm">
            {pdfError}
          </div>
        )}

        {pages.length === 0 && !pdfError && (
          <div className="w-full max-w-3xl h-64 bg-white border border-slate-200 rounded-sm flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {(pages.length > 0 || pdfError) &&
          Array.from({ length: Math.max(pages.length, 1) }).map((_, i) => (
            <div key={i} style={{ maxWidth: 794, width: '100%' }}>
              <div
                ref={el => containerRefs.current[i] = el}
                className="relative bg-white shadow-md select-none"
                style={{
                  cursor: editorMode === 'fields' && selectedType ? 'crosshair'
                        : editorMode === 'annotate' && annotationTool ? 'crosshair'
                        : 'default',
                  minHeight: pdfError ? 200 : undefined,
                }}
                onClick={e => handlePageClick(e, i)}
                data-testid={`pdf-page-${i}`}
              >
                {pages[i]
                  ? <canvas ref={el => canvasRefs.current[i] = el} className="w-full block" />
                  : <div className="h-48 flex items-center justify-center bg-slate-50 text-slate-400 text-sm">Page {i + 1}</div>
                }

                {/* ─ Annotation layer ─ */}
                <AnnotationLayer
                  pageIdx={i}
                  annotations={annotations.filter(a => a.page === i)}
                  activeTool={activeAnnotationTool}
                  activeColor={annotationColor}
                  strokeWidth={strokeWidth}
                  onAdd={handleAddAnnotation}
                  onRemove={handleRemoveAnnotation}
                  pdfCanvasRef={{ current: canvasRefs.current[i] }}
                  active={editorMode === 'annotate' && !!annotationTool}
                />

                {/* ─ Form field overlays ─ */}
                {fields.filter(f => f.page === i).map(field => {
                  const fDef = FIELD_TYPES.find(ft => ft.type === field.type);
                  return (
                    <div
                      key={field.id}
                      className={`field-overlay ${selectedField?.id === field.id ? 'selected' : ''}`}
                      style={{
                        left: `${field.x}%`, top: `${field.y}%`,
                        width: `${field.width}%`, height: `${field.height}%`,
                        borderColor: fDef?.color || '#1d4ed8',
                        background: fDef?.bg || 'rgba(29,78,216,0.1)',
                        color: fDef?.color || '#1d4ed8',
                        position: 'absolute',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 10,
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        setSelectedField(field);
                        setSelectedType(null);
                        setAnnotationTool(null);
                        if (editorMode !== 'fields') setEditorMode('fields');
                      }}
                      data-testid={`field-${field.id}`}
                    >
                      <span className="text-xs font-semibold truncate px-1">{field.type}</span>
                      {!field.required && <span className="text-xs opacity-60 ml-0.5">(opt)</span>}
                    </div>
                  );
                })}
              </div>
              <p className="text-center text-xs text-slate-400 mt-2">Page {i + 1}</p>
            </div>
          ))
        }
      </div>
    </div>
  );
}
